import api from "../../../../services/axios";
import {
  ClientTicket,
  ClientTicketSla,
  ClientTicketCategory,
  ClientTicketListFilter,
  OpenTicketRequest,
} from "./ticketTypes";

const BASE = "/ClientPortal/tickets";

/**
 * ⚠ Diferente de `getOverview`/`getProjects`/etc.: as rotas de chamado do `ClientPortalController`
 * NÃO aceitam `clientId`, nem na query nem no corpo — de propósito (design D6). O cliente é sempre
 * resolvido no servidor a partir do `ClientUserEntity` do usuário autenticado. Mandar o parâmetro
 * aqui não quebraria nada tecnicamente (o binding simplesmente ignoraria), mas sugeriria que ele faz
 * algo — e um dia alguém confiaria nisso para pré-visualizar chamados de outro cliente.
 */
export async function listClientTickets(filter: ClientTicketListFilter): Promise<ClientTicket[]> {
  const { data } = await api.get(BASE, { params: { state: filter } });
  return Array.isArray(data) ? data : [];
}

export async function getClientTicket(id: number): Promise<ClientTicket> {
  const { data } = await api.get(`${BASE}/${id}`);
  return data;
}

/**
 * Abre um chamado em nome do cliente logado. Nunca manda `clientId` — o corpo só carrega o que o
 * cliente de fato decide (assunto, descrição, impacto, urgência); prioridade é derivada no servidor.
 */
export async function openClientTicket(payload: OpenTicketRequest): Promise<ClientTicket> {
  const { data } = await api.post(BASE, payload);
  return data;
}

/**
 * Catálogo ativo de categorias para o formulário de abertura.
 *
 * ⚠ Rota **do portal**, não `GET /api/TicketCategory`: aquela está atrás de `tickets.view`, e
 * conceder essa permissão ao perfil `Client` entregaria, no mesmo clique, os chamados de todos os
 * clientes e as notas internas de todos eles. O catálogo é o mesmo dado; o caminho até ele é que
 * não pode ser.
 */
export async function listClientTicketCategories(): Promise<ClientTicketCategory[]> {
  const { data } = await api.get("/ClientPortal/ticket-categories");
  return Array.isArray(data) ? data : [];
}

function formDeArquivos(arquivos: File[], body?: string): FormData {
  const form = new FormData();
  if (body !== undefined) form.append("body", body);
  arquivos.forEach((a) => form.append("arquivos", a));
  return form;
}

/** Mensagem pública com texto e/ou arquivos — o portal nunca manda `isInternal`. */
export async function sendClientTicketMessage(
  ticketId: number,
  req: { body: string; arquivos: File[] }
): Promise<void> {
  await api.post(`${BASE}/${ticketId}/mensagens`, formDeArquivos(req.arquivos, req.body));
}

/** Sobe arquivos no chamado recém-aberto (a abertura continua JSON). */
export async function uploadClientTicketAttachments(ticketId: number, arquivos: File[]): Promise<void> {
  await api.post(`${BASE}/${ticketId}/attachments`, formDeArquivos(arquivos));
}

/** Download pelo portal: Bearer exige blob + URL de objeto (link simples não carrega o token). */
export async function downloadClientTicketAttachment(
  ticketId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await api.get(`${BASE}/${ticketId}/attachments/${attachmentId}`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(data as Blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Adiado: revogar na hora pode cancelar o download em alguns navegadores.
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
}

/**
 * Indicador de SLA do cliente logado, opcionalmente de um mês ("yyyy-MM"). Como as demais rotas de
 * chamado do portal, NÃO manda `clientId` nem responsável: o escopo vem do login.
 */
export async function getClientTicketSla(month?: string | null): Promise<ClientTicketSla> {
  const { data } = await api.get(`${BASE}/sla-compliance`, { params: { month: month ?? undefined } });
  return data;
}
